/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState, useContext, useRef } from 'react'

// react-router-dom components
import { useLocation, NavLink } from 'react-router-dom'

// prop-types is a library for typechecking of props.
import PropTypes from 'prop-types'

// @mui material components
import List from '@mui/material/List'
import Divider from '@mui/material/Divider'
import Link from '@mui/material/Link'
import Icon from '@mui/material/Icon'

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import MDButton from 'components/MDButton'

// Material Dashboard 2 PRO React examples
import SidenavCollapse from 'examples/Sidenav/SidenavCollapse'
import SidenavList from 'examples/Sidenav/SidenavList'
import SidenavItem from 'examples/Sidenav/SidenavItem'

// Custom styles for the Sidenav
import SidenavRoot from 'examples/Sidenav/SidenavRoot'
import sidenavLogoLabel from 'examples/Sidenav/styles/sidenav'
import CrudService from 'services/cruds-service'

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setWidthnav,
  AuthContext,
} from 'context'

import AuthService from 'services/auth-service'
import { Can } from 'Can'
import smallLogo from 'assets/images/logos/icon.svg'
import { useWebSocket } from 'context/WebSocketContext'

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { setIsAuthenticated, getCurrentUser } = useContext(AuthContext)
  const authContext = useContext(AuthContext)

  const [openCollapse, setOpenCollapse] = useState(false)
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false)
  const [openDeepNestedCollapse, setOpenDeepNestedCollapse] = useState(false)
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, transparentSidenav, whiteSidenav, widthnav, darkMode } = controller
  const location = useLocation()
  const { pathname } = location
  const collapseName = pathname.split('/').slice(1)[0]
  const items = pathname.split('/').slice(1)
  const itemParentName = items[1]
  const itemName = items[items.length - 1]
  const [devices, setDevices] = useState([])
  const [devicesId, setDeviceId] = useState([])
  const { isAuthenticated } = useContext(AuthContext)
  const sideNavRef = useRef(null);
  const { assetTrackerData, levelMonitorData, alertData, holyKelData } = useWebSocket();

  let textColor = 'white'

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = 'dark'
  } else if (whiteSidenav && darkMode) {
    textColor = 'inherit'
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true)

  useEffect(() => {
    setOpenCollapse(collapseName)
    setOpenNestedCollapse(itemParentName)
  }, [])

  const fetchData = async () => {
    try {
      const response = await CrudService.getSidebarDevices()
      setDevices(response.data)
      setDeviceId(response.data.map((e) => e.id))
    } catch (error) {
      // Handle errors here, e.g., log the error or show a notification
      console.error('Error fetching internal devices:', error)
    }
  }

  function rewritePercent(percent){
    let ops = ['-', null, 'null'].includes(percent) ? '' : '%';
    return `${percent}${ops}`
  }
  useEffect(() => {
    if (isAuthenticated) {
      fetchData()
    }
  }, [isAuthenticated])

  useEffect(() => {
    if(alertData && alertData.device_id && alertData?.alert?.AlertType){
      // fetchSingleData(alertData.device_id);
      const content = document.getElementById(`alert_noti_${alertData.device_id}`);
      const type = alertData?.alert?.AlertType;
      if(content && type){
        if(alertData.type === 'Heal'){
          content.className = '';
        } else {
          content.className = type?.Icon?.class_name ?? 'fas fa-triangle-exclamation';
          content.style.color = type?.BadgeDetail?.font_color_hex ?? 'red'
        }
      }
    }
  }, [alertData])

  useEffect(() => {
    if (holyKelData && holyKelData?.percent) {
        const content = document.getElementById(`percent_${holyKelData.device_id}`);
        if (content) {
          const percent = rewritePercent(holyKelData?.percent)
          content.innerText = percent;
          content.title = percent;
        }
    }
  }, [holyKelData])

  useEffect(() => {
    if (levelMonitorData && levelMonitorData?.data?.reportType != '03') {
      if (levelMonitorData?.data?.device_data?.percent_level) {
        const content = document.getElementById(`percent_${levelMonitorData.device_id}`);
        if (content) {
          const percent = rewritePercent(levelMonitorData?.data?.device_data?.percent_level)
          content.innerText = percent;
          content.title = percent;
        }
      }
    }
  }, [levelMonitorData])

  useEffect(() => {
    if (assetTrackerData && assetTrackerData?.geofence) {
      if (assetTrackerData.geofence) {
        const content = document.getElementById(`geofence_${assetTrackerData.device_id}`);
        if (content) {
          content.style.backgroundColor = assetTrackerData.geofence.color;
          content.title = assetTrackerData.geofence.name;
        }
      } else {
        const content = document.getElementById(`geofence_${assetTrackerData.device_id}`);
        if (content) {
          content.style.backgroundColor = '';
          content.title = '';
        }
      }
    }
  }, [assetTrackerData]);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200)
      setTransparentSidenav(
        dispatch,
        window.innerWidth < 1200 ? false : transparentSidenav,
      )
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav)
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener('resize', handleMiniSidenav)

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav()

    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleMiniSidenav)
  }, [dispatch, location])

  useEffect(() => {
    const sideNavElement = sideNavRef.current;

    if (!sideNavElement) return;

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setWidthnav(dispatch, entry.contentRect.width); // Update the width
      }
    });

    resizeObserver.observe(sideNavElement);

    // Cleanup observer on unmount
    return () => resizeObserver.disconnect();
  }, []);

  const renderDeviceItems = devices.map((device, index) => {
    let deviceParamValue = device.paramValue
      ? device.paramValue?.value
      : undefined
    let percent = deviceParamValue ? (deviceParamValue?.is_error ? '-' : deviceParamValue.device_data?.percent_level) : 0
    if(deviceParamValue?.channels){
      let channels = Object.keys(deviceParamValue.channels)
      let mul_percent = channels.map(e => deviceParamValue.channels?.[e]?.current_value ?? 0);
      percent = mul_percent.join(', ')
    }
    let alert = device.alert

    return (
      <NavLink
        to={`/devices/view-device/${device.id}`}
        key={index}
      >
        <SidenavItem
          key={index}
          icon={device?.attributes?.EntityAddressItem?.Icon?.class_name ?? null}
          name={device?.attributes?.name}
          percent={percent}
          route={`/devices/view-device/${device.id}`}
          // icon={<Icon>devices</Icon>}
          device_id={device.id}
          geofence={device?.attributes?.GeofenceExist?.Geofence ?? null}
          active={
            pathname === `/devices/view-device/${device.id}`
          }
          alert={alert}
          isNameNeed={false}
        />
      </NavLink>
    )
  })

  const handleLogOut = async () => {
    try {
      let user = await getCurrentUser()
      await AuthService.logout(user)
      authContext.logout()
    } catch (err) {
      console.error(err)
      return null
    }
  }
  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapses) => {
    const template = collapses.map(({ name, route, key, href, type, collapse }, index) =>
      href ? (
        <Can I="view" this={type} key={index}>
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: 'none' }}
          >
            <SidenavItem name={name} nested />
          </Link>
        </Can>
      ) : (
        collapse && collapse.length ?
          deepNestedRenderCollapse({ name, route, key, href, type, collapse, index })
          :
          <Can I="view" this={type} key={index}>
            <NavLink to={route} key={key} sx={{ textDecoration: 'none' }}>
              <SidenavItem name={name} active={route === pathname} nested />
            </NavLink>
          </Can>
      ),
    )

    return template
  }

  const deepNestedRenderCollapse = ({ name, collapse, route, href, key, type, index }) => {
    let returnValue
    returnValue = (
      <Can I="view" this={type} key={index}>
        <SidenavItem
          key={key}
          color={color}
          name={name}
          active={key === itemParentName ? 'isParent' : false}
          open={openDeepNestedCollapse === key}
          onClick={({ currentTarget }) =>
            openDeepNestedCollapse === key &&
              currentTarget.classList.contains('MuiListItem-root')
              ? setOpenDeepNestedCollapse(false)
              : setOpenDeepNestedCollapse(key)
          }
        >
          {renderNestedCollapse(collapse)}
        </SidenavItem>
      </Can>
    )
    return <SidenavList key={key}>{returnValue}</SidenavList>
  }

  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key, type }, index) => {
      let returnValue
      if (collapse) {
        returnValue = (
          <Can I="view" this={type} key={index}>
            <SidenavItem
              key={key}
              color={color}
              name={name}
              active={key === itemParentName ? 'isParent' : false}
              open={openNestedCollapse === key}
              onClick={({ currentTarget }) =>
                openNestedCollapse === key &&
                  currentTarget.classList.contains('MuiListItem-root')
                  ? setOpenNestedCollapse(false)
                  : setOpenNestedCollapse(key)
              }
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          </Can>
        )
      } else {
        if (name !== 'Logout') {
          returnValue = href ? (
            type ? (
              <Can I="edit" this={type} key={index}>
                <Link
                  href={href}
                  key={key}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ textDecoration: 'none' }}
                >
                  <SidenavItem
                    color={color}
                    name={name}
                    active={key === itemName}
                  />
                </Link>
              </Can>
            ) : (
              <Link
                href={href}
                key={key}
                target="_blank"
                rel="noreferrer"
                sx={{ textDecoration: 'none' }}
              >
                <SidenavItem
                  color={color}
                  name={name}
                  active={key === itemName}
                />
              </Link>
            )
          ) : type ? (
            <Can I="edit" a={type} key={index}>
              <NavLink to={route} key={key} sx={{ textDecoration: 'none' }}>
                <SidenavItem
                  color={color}
                  name={name}
                  active={key === itemName}
                />
              </NavLink>
            </Can>
          ) : (
            <NavLink to={route} key={key} sx={{ textDecoration: 'none' }}>
              <SidenavItem
                color={color}
                name={name}
                active={key === itemName}
              />
            </NavLink>
          )
        } else {
          returnValue = (
            <MDBox key={index}>
              <MDButton
                fullWidth
                variant="gradient"
                color={color}
                type="button"
                onClick={handleLogOut}
              >
                Log Out
              </MDButton>
            </MDBox>
          )
        }
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>
    })

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
      let returnValue
      if (type === 'collapse') {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: 'none' }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          )
        } else if (noCollapse && route) {
          returnValue = (
            <Can I="view" a={key} key={key}>
              <NavLink to={route} key={key}>
                <SidenavCollapse
                  name={name}
                  icon={icon}
                  noCollapse={noCollapse}
                  active={key === collapseName}
                >
                  {collapse ? renderCollapse(collapse) : null}
                </SidenavCollapse>
              </NavLink>
            </Can>
          )
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() =>
                openCollapse === key
                  ? setOpenCollapse(false)
                  : setOpenCollapse(key)
              }
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          )
        }
      } else if (type === 'title') {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            ml={1}
          >
            {title}
          </MDTypography>
        )
      } else if (type === 'divider') {
        returnValue = (
          <Divider
            key={key}
            sx={{ margin: 0, padding: 0 }}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        )
      }

      return returnValue
    },
  )

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={1} px={4} textAlign="center" ref={sideNavRef}>
        <MDBox
          display={{ xs: 'block', xl: 'none' }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: 'pointer' }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ marginLeft: '-8px', marginTop: '20px' }}
        >
          {brand && (
            <MDBox
              component="img"
              src={miniSidenav ? smallLogo : brand}
              alt="Brand"
              width="8rem"
              height="3rem"
              style={{ paddingLeft: miniSidenav ? '16px' : '0' }}
            />
          )}
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>
        {renderRoutes}
        <Divider />

        <SidenavCollapse
          key="devices"
          name="Devices"
          icon={<Icon>devices</Icon>}
          active={collapseName === 'devices'} // Update with the appropriate key for the "Devices" section
          open={openCollapse === 'devices'} // Update with the appropriate key for the "Devices" section
          onClick={() =>
            openCollapse === 'devices'
              ? setOpenCollapse(false)
              : setOpenCollapse('devices')
          }
        >
          {renderDeviceItems}
        </SidenavCollapse>
      </List>
    </SidenavRoot>
  )
}

// Setting default values for the props of Sidenav
// Sidenav.defaultProps = {
//   color: 'info',
//   brand: '',
// }

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
    'dark',
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default Sidenav
